import React, { useState } from 'react';
import MaterialIcon from '../ui/MaterialIcon';
import { LOYALTY_PROGRAMS_BY_ID } from '../../data/loyaltyPrograms';
import { useLoyalty } from '../../contexts/LoyaltyContext';

const ACTIVITY_TEMPLATES = {
  airline: [
    'Flight redemption',
    'Partner hotel transfer',
    'Shopping portal purchase',
    'Dining bonus',
    'Bonus promotion',
    'Status challenge',
  ],
  hotel: [
    'Hotel stay',
    'Room upgrade redemption',
    'Airline transfer',
    'Dining at property',
    'Bonus promotion',
    'Partner purchase',
  ],
  retail: [
    'In-store purchase',
    'Online purchase',
    'Birthday bonus',
    'Referral bonus',
    'Redemption',
    'Promo reward',
  ],
};

function AddActivityModal({ onClose }) {
  const { programs, logActivity } = useLoyalty();
  const [selectedProgramId, setSelectedProgramId] = useState(programs[0]?.programId || '');
  const [type, setType] = useState('earned');
  const [points, setPoints] = useState('');
  const [description, setDescription] = useState('');

  const selectedEnrolled = programs.find((p) => p.programId === selectedProgramId);
  const programInfo = LOYALTY_PROGRAMS_BY_ID.get(selectedProgramId);
  const templates = programInfo ? ACTIVITY_TEMPLATES[programInfo.category] || [] : [];

  const handleSave = () => {
    if (!points || !selectedProgramId || !description) return;
    logActivity({
      programId: selectedProgramId,
      programName: programInfo?.name || selectedProgramId,
      description,
      points,
      unit: programInfo?.unit || 'points',
      type,
    });
    onClose();
  };

  if (programs.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-lg bg-surface rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 text-center">
          <MaterialIcon name="loyalty" size={48} className="text-on-surface-variant/40 mb-3" />
          <p className="text-base font-semibold text-on-surface">No programs yet</p>
          <p className="text-sm text-on-surface-variant mt-1 mb-5">Add a loyalty program first to log activity.</p>
          <button onClick={onClose} className="px-6 py-3 bg-primary text-on-primary rounded-full font-semibold text-sm">
            Got it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-surface rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[85vh]">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-outline-variant/50" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-outline-variant/20">
          <h2 className="text-lg font-bold text-on-surface">Log Activity</h2>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-surface-container transition-colors"
          >
            <MaterialIcon name="close" size={20} className="text-on-surface-variant" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Program selector */}
          <div>
            <label className="block text-sm font-semibold text-on-surface mb-2">Program</label>
            <div className="relative">
              <select
                value={selectedProgramId}
                onChange={(e) => setSelectedProgramId(e.target.value)}
                className="w-full px-4 py-3 bg-surface-container rounded-2xl text-sm text-on-surface outline-none appearance-none pr-10"
              >
                {programs.map((p) => (
                  <option key={p.programId} value={p.programId}>
                    {LOYALTY_PROGRAMS_BY_ID.get(p.programId)?.name || p.programId}
                  </option>
                ))}
              </select>
              <MaterialIcon
                name="expand_more"
                size={20}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none"
              />
            </div>

            {/* Current balance hint */}
            {selectedEnrolled && (
              <p className="text-xs text-on-surface-variant mt-1.5 px-1">
                Current balance: {selectedEnrolled.balance.toLocaleString()} {programInfo?.unit}
              </p>
            )}
          </div>

          {/* Earned / Redeemed toggle */}
          <div>
            <label className="block text-sm font-semibold text-on-surface mb-2">Type</label>
            <div className="flex gap-2">
              {['earned', 'redeemed'].map((t) => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold capitalize transition-colors ${
                    type === t
                      ? t === 'earned'
                        ? 'bg-success/90 text-white'
                        : 'bg-error/90 text-white'
                      : 'bg-surface-container text-on-surface-variant'
                  }`}
                >
                  {t === 'earned' ? '+ Earned' : '− Redeemed'}
                </button>
              ))}
            </div>
          </div>

          {/* Points amount */}
          <div>
            <label className="block text-sm font-semibold text-on-surface mb-2">
              Amount <span className="text-error">*</span>
            </label>
            <div className="flex items-center gap-3 px-4 py-3 bg-surface-container rounded-2xl">
              <input
                type="number"
                placeholder="0"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                className="flex-1 bg-transparent text-base font-semibold text-on-surface outline-none"
                min="1"
              />
              <span className="text-sm text-on-surface-variant font-medium shrink-0">
                {programInfo?.unit || 'points'}
              </span>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-on-surface mb-2">
              Description <span className="text-error">*</span>
            </label>
            <div className="px-4 py-3 bg-surface-container rounded-2xl">
              <input
                type="text"
                placeholder="e.g. Flight EWR→LAX"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full bg-transparent text-sm text-on-surface outline-none"
              />
            </div>
            {/* Quick-fill templates */}
            {templates.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {templates.map((t) => (
                  <button
                    key={t}
                    onClick={() => setDescription(t)}
                    className="px-2.5 py-1 bg-surface-container rounded-full text-xs text-on-surface-variant hover:bg-primary-light hover:text-primary transition-colors"
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={!points || !description}
            className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-primary via-primary to-primary-dark text-on-primary font-semibold text-sm active:scale-[0.97] duration-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            Log Activity
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddActivityModal;
